import React, {FC, useMemo} from "react";
import moment, {Moment} from "moment";
import {DatePicker} from "antd";
import {PickerDateProps} from "antd/lib/date-picker/generatePicker";
import useTypedPicker from "./useTypedPicker";

// Beim Tippen akzeptierte Formate, zusätzlich zum Anzeigeformat des Feldes
export const DATE_INPUT_FORMATS = ["DD.MM.YY", "DD.MM.YYYY", "D.M.YY", "D.M.YYYY"];

// Für antd-RangePicker (zwei Felder, eigene Fokus-Logik): nur die Formate, Übernahme
// macht rc-picker selbst. Konstante, damit rc-picker den Text nicht bei jedem
// Rendern zurücksetzt.
// ponytail: rc-picker übernimmt dort nur bei Enter (Tab bleibt beim ersten Mal hängen,
// Klick daneben verwirft) und bei Enter den letzten gültigen Zwischenstand
// („01.01.202“ → 01.01.2020). Abhilfe wäre eine eigene Range-Komponente.
export const DATE_RANGE_FORMATS = ["DD.MM.YYYY", ...DATE_INPUT_FORMATS.filter((format) => format !== "DD.MM.YYYY")];

// Wer in „Mi 21.01.26“ nur die Ziffern ändert, lässt einen unpassenden Wochentag
// stehen, und das strenge Parsen lehnt ihn ab. Der Wochentag als fester Text
// wird nur übersprungen, nicht geprüft.
const getFormats = (displayFormat: string, locale: string) => {
    const weekdays = [moment.localeData(locale), moment.localeData("de"), moment.localeData("en")].filter(Boolean).reduce<Array<string>>((all, data) => all.concat(data.weekdaysMin()), []);
    const uniqueWeekdays = weekdays.filter((day, index) => weekdays.indexOf(day) === index);
    const weekdayFormats = displayFormat.startsWith("dd ") ? uniqueWeekdays.reduce<Array<string>>((all, day) => all.concat(DATE_INPUT_FORMATS.map((format) => `[${day}] ${format}`)), []) : [];

    return [displayFormat, ...DATE_INPUT_FORMATS, ...weekdayFormats].filter((format, index, all) => all.indexOf(format) === index);
};

// Nur Datums-Picker (ohne Uhrzeit); DatePickerProps ist eine Union, deren Omit
// Props wie showNow verliert
export type DateInputProps = Omit<PickerDateProps<Moment>, "format" | "picker"> & {
    // Anzeigeformat; getippt werden zusätzlich DATE_INPUT_FORMATS akzeptiert
    displayFormat?: string;
};

// DatePicker, der Getipptes bei Enter, Tab und Klick daneben übernimmt (useTypedPicker)
const DateInput: FC<DateInputProps> = (props) => {
    const {displayFormat = "DD.MM.YYYY", value, defaultValue, open, onChange, onFocus, onOpenChange, onSelect, ...restProps} = props;
    // Neues Array bei jedem Rendern würde rc-picker den getippten Text zurücksetzen lassen
    const locale = moment.locale();
    const formats = useMemo(() => getFormats(displayFormat, locale), [displayFormat, locale]);
    const {disabledDate} = restProps;

    const parse = (text: string) => {
        let typed = moment(text, formats, true);
        // Am Text erkennen, nicht am Format: lokalisierte Formate wie „L“ enthalten kein YYYY
        const isTwoDigitYear = !/\d{4}/.test(text);

        // Zweistelliges Jahr, das gesperrt ist: Moment legt „60“ auf 2060, bei
        // Geburtsdaten ist 1960 gemeint
        if (typed.isValid() && isTwoDigitYear && disabledDate?.(typed)) {
            const centuryEarlier = typed.clone().subtract(100, "years");

            if (!disabledDate(centuryEarlier)) {
                typed = centuryEarlier;
            }
        }

        return typed.isValid() && !disabledDate?.(typed) ? typed : null;
    };

    const {wrapperProps, pickerProps} = useTypedPicker(props, {parse, isSame: (a, b) => a.isSame(b, "day"), format: formats[0]});

    return (
        <span {...wrapperProps}>
            <DatePicker format={formats} {...restProps} {...pickerProps} />
        </span>
    );
};

export default DateInput;
